/**
 * AdminChatPage - dashboard for technicians/admins to handle support chats
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { api, getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useChatSocket } from "@/lib/useChatSocket";
import { fmtDateTime, fmtTime, relativeFromNow } from "@/lib/date";
import StatusBadge from "@/components/shared/StatusBadge";
import {
    Send, Loader2, CheckCircle2, MessageCircle, Building2,
    User as UserIcon, Mail, Search,
} from "lucide-react";

const STATUS_OPTIONS = [
    { v: "all", label: "Todas" },
    { v: "aberta", label: "Em fila" },
    { v: "em_atendimento", label: "Em atendimento" },
    { v: "resolvida", label: "Resolvidas" },
];

export default function AdminChatPage() {
    const { user, token } = useAuth();
    const [list, setList] = useState([]);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [selId, setSelId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [presence, setPresence] = useState({ online_count: 0, online_names: [] });
    const scrollRef = useRef(null);

    const sel = list.find((c) => c.id === selId) || null;

    const onEvent = async (evt) => {
        if (evt.type === "new_message") {
            // Update list - upsert and resort
            setList((curr) => {
                const idx = curr.findIndex((c) => c.id === evt.conversation.id);
                if (idx >= 0) {
                    const copy = [...curr];
                    copy[idx] = evt.conversation;
                    return copy.sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0));
                }
                return [evt.conversation, ...curr];
            });
            if (evt.conversation.id === selId) {
                setMessages((m) => [...m, evt.message]);
                api.post(`/chat/conversations/${selId}/read`).catch(() => {});
            }
        } else if (evt.type === "presence") {
            setPresence({ online_count: evt.online_count, online_names: evt.online_names });
        } else if (evt.type === "conversation_updated") {
            setList((curr) => curr.map((c) => (c.id === evt.conversation.id ? evt.conversation : c)));
        }
    };
    const { status, send } = useChatSocket({ enabled: !!user, token, onEvent });

    const load = async () => {
        const { data } = await api.get("/chat/conversations");
        setList(data);
    };
    useEffect(() => { load(); }, []);

    useEffect(() => {
        api.get("/chat/agents/online").then((r) => setPresence(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!selId) { setMessages([]); return; }
        api.get(`/chat/conversations/${selId}/messages`).then((r) => {
            setMessages(r.data);
            api.post(`/chat/conversations/${selId}/read`).catch(() => {});
            setList((curr) => curr.map((c) => (c.id === selId ? { ...c, unread_for_agent: 0 } : c)));
        });
    }, [selId]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    const filtered = useMemo(() => {
        return list.filter((c) => {
            if (filter !== "all" && c.status !== filter) return false;
            if (search) {
                const s = search.toLowerCase();
                if (!`${c.client_name} ${c.client_email} ${c.client_company || ""}`.toLowerCase().includes(s)) return false;
            }
            return true;
        });
    }, [list, filter, search]);

    const submit = async (e) => {
        e?.preventDefault?.();
        const body = input.trim();
        if (!body || !sel) return;
        setSending(true);
        const optimistic = {
            id: `tmp-${Date.now()}`,
            sender_role: "agent",
            sender_id: user.id,
            sender_name: user.name,
            body,
            created_at: new Date().toISOString(),
        };
        setMessages((m) => [...m, optimistic]);
        setInput("");
        try {
            const sent = send({ type: "send_message", conversation_id: sel.id, body });
            if (!sent) {
                await api.post(`/chat/conversations/${sel.id}/messages`, { body });
            }
        } catch (err) {
            toast.error(getApiErrorMessage(err));
            setMessages((m) => m.filter((x) => x.id !== optimistic.id));
        } finally {
            setSending(false);
        }
    };

    const assignToMe = async () => {
        if (!sel) return;
        try {
            await api.post(`/chat/conversations/${sel.id}/assign`);
            toast.success("Conversa atribuída a si");
            await load();
        } catch (e) { toast.error(getApiErrorMessage(e)); }
    };

    const closeConv = async () => {
        if (!sel) return;
        if (!window.confirm("Marcar conversa como resolvida?")) return;
        try {
            await api.post(`/chat/conversations/${sel.id}/close`);
            toast.success("Conversa resolvida");
            await load();
        } catch (e) { toast.error(getApiErrorMessage(e)); }
    };

    return (
        <div className="space-y-4" data-testid="admin-chat-page">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Chat de Suporte</h1>
                    <p className="text-muted-foreground">
                        {presence.online_count} técnico(s) online · {list.length} conversa(s) total
                        <span className={`ml-2 text-xs ${status === "connected" ? "text-[#2DD4BF]" : "text-muted-foreground"}`}>
                            ● {status === "connected" ? "em tempo real" : status === "connecting" ? "a ligar..." : "offline"}
                        </span>
                    </p>
                </div>
            </div>

            <div className="grid lg:grid-cols-[360px_1fr] gap-4 h-[calc(100dvh-220px)] min-h-[480px]">
                {/* LEFT: list */}
                <Card className="glass-card rounded-2xl flex flex-col overflow-hidden">
                    <div className="p-3 border-b border-white/10 space-y-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Pesquisar cliente..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9 h-9"
                                data-testid="admin-chat-search"
                            />
                        </div>
                        <Tabs value={filter} onValueChange={setFilter}>
                            <TabsList className="w-full grid grid-cols-4 h-8">
                                {STATUS_OPTIONS.map((s) => (
                                    <TabsTrigger key={s.v} value={s.v} className="text-xs" data-testid={`admin-chat-filter-${s.v}`}>
                                        {s.label}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </Tabs>
                    </div>
                    <div className="flex-1 overflow-y-auto scrollbar-thin">
                        {filtered.length === 0 ? (
                            <div className="p-6 text-center text-sm text-muted-foreground">Sem conversas.</div>
                        ) : (
                            filtered.map((c) => (
                                <button
                                    key={c.id}
                                    onClick={() => setSelId(c.id)}
                                    className={`w-full text-left p-3 border-b border-white/5 hover:bg-white/5 ${
                                        selId === c.id ? "bg-[#6B21D9]/10" : ""
                                    }`}
                                    data-testid={`admin-chat-conv-${c.id}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span className="h-8 w-8 rounded-full bg-gradient-to-br from-[#6B21D9] to-[#A78BFA] grid place-items-center text-white text-xs font-bold shrink-0 relative">
                                                {(c.client_name || "?")[0]}
                                                {c.client_online && (
                                                    <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-[#2DD4BF] border-2 border-card" />
                                                )}
                                            </span>
                                            <div className="min-w-0">
                                                <div className="font-medium text-sm truncate">{c.client_name}</div>
                                                <div className="text-[11px] text-muted-foreground truncate">{c.client_company || c.client_email}</div>
                                            </div>
                                        </div>
                                        {c.unread_for_agent > 0 && (
                                            <Badge className="bg-[#FB7185] text-white border-none text-[10px] h-5 min-w-5 grid place-items-center">
                                                {c.unread_for_agent}
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="mt-1 text-xs text-muted-foreground line-clamp-1">{c.last_message || "— sem mensagens —"}</p>
                                    <div className="mt-1 flex items-center justify-between">
                                        <StatusBadge status={c.status === "em_atendimento" ? "em_curso" : c.status === "resolvida" ? "resolvido" : "aberto"} />
                                        <span className="text-[10px] text-muted-foreground">{c.last_message_at ? relativeFromNow(c.last_message_at) : ""}</span>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </Card>

                {/* RIGHT: thread */}
                <Card className="glass-card rounded-2xl flex flex-col overflow-hidden">
                    {!sel ? (
                        <div className="flex-1 grid place-items-center text-center text-muted-foreground p-8">
                            <div>
                                <MessageCircle className="h-10 w-10 mx-auto mb-3 text-[#A78BFA]" />
                                <p>Selecione uma conversa à esquerda</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3 flex-wrap">
                                <span className="h-10 w-10 rounded-full bg-gradient-to-br from-[#6B21D9] to-[#A78BFA] grid place-items-center text-white font-bold relative">
                                    {(sel.client_name || "?")[0]}
                                    {sel.client_online && (
                                        <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-[#2DD4BF] border-2 border-card" />
                                    )}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-semibold flex items-center gap-2">
                                        {sel.client_name}
                                        <StatusBadge status={sel.status === "em_atendimento" ? "em_curso" : sel.status === "resolvida" ? "resolvido" : "aberto"} />
                                    </div>
                                    <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                                        {sel.client_company && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" /> {sel.client_company}</span>}
                                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {sel.client_email}</span>
                                        {sel.agent_name && <span className="flex items-center gap-1"><UserIcon className="h-3 w-3" /> Atendido por {sel.agent_name}</span>}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {(!sel.agent_id || sel.agent_id !== user.id) && sel.status !== "resolvida" && (
                                        <Button variant="outline" size="sm" onClick={assignToMe} data-testid="admin-chat-assign-button">
                                            Atender eu
                                        </Button>
                                    )}
                                    {sel.status !== "resolvida" && (
                                        <Button variant="outline" size="sm" onClick={closeConv} data-testid="admin-chat-close-button">
                                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Resolver
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin px-4 py-3 space-y-2">
                                {messages.map((m) => (
                                    <MessageBubble key={m.id} msg={m} isMine={m.sender_role === "agent" && m.sender_id === user.id} isAgent={m.sender_role === "agent"} />
                                ))}
                                {messages.length === 0 && (
                                    <div className="text-center text-sm text-muted-foreground py-8">Sem mensagens.</div>
                                )}
                            </div>

                            <form onSubmit={submit} className="border-t border-white/10 p-3 flex gap-2">
                                <Input
                                    placeholder={sel.status === "resolvida" ? "Conversa resolvida" : "Escreva uma resposta..."}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    disabled={sel.status === "resolvida" || sending}
                                    data-testid="admin-chat-input"
                                />
                                <Button
                                    type="submit"
                                    className="btn-grad-purple text-white"
                                    disabled={!input.trim() || sel.status === "resolvida" || sending}
                                    data-testid="admin-chat-send-button"
                                >
                                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                </Button>
                            </form>
                        </>
                    )}
                </Card>
            </div>
        </div>
    );
}

function MessageBubble({ msg, isMine, isAgent }) {
    return (
        <div className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
            <div
                className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${
                    isMine
                        ? "btn-grad-purple text-white rounded-br-md"
                        : isAgent
                            ? "bg-[#6B21D9]/15 border border-[#6B21D9]/30 rounded-bl-md"
                            : "bg-white/8 border border-white/10 rounded-bl-md"
                }`}
            >
                {!isMine && (
                    <div className="text-[11px] font-semibold text-[#A78BFA] mb-0.5">
                        {msg.sender_name} {isAgent && " · técnico"}
                    </div>
                )}
                <div className="whitespace-pre-line break-words">{msg.body}</div>
                <div className={`text-[10px] mt-1 ${isMine ? "text-white/70" : "text-muted-foreground"}`}>
                    {fmtTime(msg.created_at)}
                </div>
            </div>
        </div>
    );
}
